<template>
    <analytics-stat-card :loading="loading" :error="Boolean(error)" class="vx-flex-grow" :value="value">
        <template v-slot:title>
            <slot name="title">{{ resource.title }}</slot>
        </template>
        <template v-slot:subtitle>
            <slot name="subtitle"></slot>
        </template>

        <span>{{ vxFormatCount(value) }}</span>

        <template v-slot:explainHeader>{{ resource.title }}</template>
        <template v-slot:explainContent>
            <p>{{ resource.explainContent }}</p>

            <vx-button :href="resource.explainLink" size="sm" color="muted">
                {{ resource.explainButtonText }}
                <font-awesome-icon :icon="faArrowRight"></font-awesome-icon>
            </vx-button>
        </template>
    </analytics-stat-card>
</template>

<script setup>
import { faArrowRight } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import AnalyticsStatCard from '../../partial/AnalyticsStatCard.vue';
import { VxButton, vxFormatCount } from '@voxie/frontend-components';
import { analyticsClient } from '~/services';
import { watch } from 'vue';
import { useVersionedRequest } from '~/composables/useVersionedRequest';
import { computed } from 'vue';
import { metricsInputValidator } from '~/utils/analytics';

const props = defineProps({
    fetchParams: {
        type: Object,
        required: true,
        validator: metricsInputValidator,
    },
    resourceType: {
        type: String,
        validator: (resourceType) => ['user', 'campaign', 'automation', 'playbook'].includes(resourceType),
        required: true,
    },
});

const teamId = Spark.state.currentTeam.id;

const { loading, response, error, fetchData } = useVersionedRequest();

const resource = computed(() => {
    switch (props.resourceType) {
        case 'user':
            return {
                title: 'Active Users',
                explainContent:
                    'Number of Message Hub Agents responsible for sending Messages in the selected period of time.',
                explainLink: '/messaging/messagehub',
                explainButtonText: 'Go to Message Hub',
            };
        case 'campaign':
            return {
                title: 'Active Campaigns',
                explainContent: 'Number of Campaigns that have been Live Campaigns in the selected period of time.',
                explainLink: '/messaging/campaigns',
                explainButtonText: 'Go to Campaigns',
            };
        case 'automation':
            return {
                title: 'Active Automations',
                explainContent:
                    'Number of Automations Active that have at least an Action that sends a Message in the selected period of time.',
                explainLink: '/automation/event',
                explainButtonText: 'Go to Automations',
            };
        case 'playbook':
            return {
                title: 'Active Playbooks',
                explainContent:
                    'Number of Playbooks Active that are Running and responsible for Messages Sent in the selected period of time.',
                explainLink: '/automation/flows',
                explainButtonText: 'Go to Playbooks',
            };
        default:
            return {
                title: 'Unknown',
                explainContent: 'unknown',
                explainLink: '/',
                explainButtonText: 'Go to',
            };
    }
});

const value = computed(() =>
    !error.value && Array.isArray(response.value?.data?.data?.[0]?.values)
        ? response.value?.data?.data?.[0]?.values[0]
        : undefined
);

const disabled = computed(() => {
    return (
        props.fetchParams.filters.resource_types?.length > 0 &&
        !props.fetchParams.filters.resource_types?.includes(props.resourceType)
    );
});

const params = computed(() => {
    return {
        range: props.fetchParams.range,
        filters: {
            ...props.fetchParams.filters,
            resource_types: [props.resourceType],
        },
    };
});

watch(
    () => props.fetchParams,
    () => {
        if (disabled.value) {
            response.value = undefined;
            return;
        }

        fetchData(analyticsClient.outboundMessagesTotalActiveResourcesByResourceType(teamId, params.value)).catch(
            (e) => {
                console.error(e);
            }
        );
    },
    { immediate: true, deep: true }
);

defineExpose({
    value,
    loading,
});
</script>
